import { type ReactNode, createContext, useCallback, useContext, useState } from "react";

/**
 * Validation state.
 */
export interface ValidationState {
	errors: Map<string, string>;
	touched: Set<string>;
}

/**
 * Validation context type.
 */
export interface ValidationContextType {
	/** Get error for field */
	getError: (fieldId: string) => string | null;
	/** Set error for field */
	setError: (fieldId: string, error: string | null) => void;
	/** Mark field as touched */
	touch: (fieldId: string) => void;
	/** Check if field is touched */
	isTouched: (fieldId: string) => boolean;
	/** Clear all errors */
	clearErrors: () => void;
	/** Clear error for specific field */
	clearError: (fieldId: string) => void;
	/** Validate all fields */
	validateAll: () => boolean;
	/** Check if form is valid */
	isValid: boolean;
	/** Get all errors */
	getAllErrors: () => Map<string, string>;
	/** Set multiple errors at once */
	setErrors: (errors: Record<string, string>) => void;
}

const ValidationContext = createContext<ValidationContextType | null>(null);

/**
 * Hook to access validation context.
 *
 * @returns ValidationContextType
 * @throws Error if used outside ValidationProvider
 */
export function useValidationContext(): ValidationContextType {
	const context = useContext(ValidationContext);
	if (!context) {
		throw new Error("useValidationContext must be used within ValidationProvider");
	}
	return context;
}

/**
 * Hook to access validation context (optional).
 *
 * @returns ValidationContextType | null
 */
export function useValidationContextOptional(): ValidationContextType | null {
	return useContext(ValidationContext);
}

/**
 * Props for ValidationProvider.
 */
export interface ValidationProviderProps {
	children: ReactNode;
}

/**
 * Provider for validation context.
 */
export function ValidationProvider({ children }: ValidationProviderProps) {
	const [state, setState] = useState<ValidationState>({
		errors: new Map(),
		touched: new Set(),
	});

	const getError = useCallback((fieldId: string) => state.errors.get(fieldId) || null, [state.errors]);

	const setError = useCallback((fieldId: string, error: string | null) => {
		setState((prev) => {
			const errors = new Map(prev.errors);
			if (error) {
				errors.set(fieldId, error);
			} else {
				errors.delete(fieldId);
			}
			return { ...prev, errors };
		});
	}, []);

	const setErrors = useCallback((errors: Record<string, string>) => {
		setState((prev) => {
			const newErrors = new Map(prev.errors);
			for (const [fieldId, error] of Object.entries(errors)) {
				if (error) {
					newErrors.set(fieldId, error);
				} else {
					newErrors.delete(fieldId);
				}
			}
			return { ...prev, errors: newErrors };
		});
	}, []);

	const touch = useCallback((fieldId: string) => {
		setState((prev) => {
			const touched = new Set(prev.touched);
			touched.add(fieldId);
			return { ...prev, touched };
		});
	}, []);

	const isTouched = useCallback((fieldId: string) => state.touched.has(fieldId), [state.touched]);

	const clearErrors = useCallback(() => {
		setState({ errors: new Map(), touched: new Set() });
	}, []);

	const clearError = useCallback((fieldId: string) => {
		setState((prev) => {
			const errors = new Map(prev.errors);
			errors.delete(fieldId);
			return { ...prev, errors };
		});
	}, []);

	const validateAll = useCallback(() => {
		// Trigger validation for all fields
		// Return true if valid
		return state.errors.size === 0;
	}, [state.errors]);

	const getAllErrors = useCallback(() => {
		return new Map(state.errors);
	}, [state.errors]);

	const value: ValidationContextType = {
		getError,
		setError,
		setErrors,
		touch,
		isTouched,
		clearErrors,
		clearError,
		validateAll,
		getAllErrors,
		isValid: state.errors.size === 0,
	};

	return <ValidationContext.Provider value={value}>{children}</ValidationContext.Provider>;
}
